/**
 * sseHub — singleton Server-Sent Events multiplexer.
 *
 * The TucsonStreets frontend used to open one EventSource per consumer
 * (App.tsx for map markers, usePostFeed for the Feed). Browsers cap ~6 SSE
 * connections per origin; doing it twice burned a slot for no reason and
 * forced every payload through `JSON.parse` twice. This module funnels every
 * subscriber through a single EventSource and fans events out by name.
 *
 * Lifecycle:
 *   • Connection opens lazily on first `subscribe(...)`.
 *   • When the last subscriber unsubscribes we wait `CLOSE_GRACE_MS` before
 *     tearing down — short route changes don't churn the socket.
 *   • EventSource reconnects on its own; we expose an `online` flag via
 *     `subscribeStatus` so consumers can fall back to polling while down.
 *
 * No React inside this file — it's plain TS so non-React callers can use it
 * too.
 */
export type SSEHandler<T = any> = (data: T) => void;
export type StatusHandler = (online: boolean) => void;

const STREAM_URL = '/api/stream/posts';
const CLOSE_GRACE_MS = 5_000;

type Listener = SSEHandler<any>;

const listeners = new Map<string, Set<Listener>>();
const statusListeners = new Set<StatusHandler>();
const eventSourceListeners = new Map<string, (e: MessageEvent) => void>();

let es: EventSource | null = null;
let online = false;
let closeTimer: number | null = null;

function totalSubscriberCount(): number {
  let n = 0;
  for (const set of listeners.values()) n += set.size;
  return n;
}

function setOnline(next: boolean) {
  if (online === next) return;
  online = next;
  for (const fn of statusListeners) {
    try { fn(online); } catch { /* listener errors are not our problem */ }
  }
}

/** Attach a native EventSource listener for a given event name (once). */
function ensureNativeListener(eventType: string) {
  if (!es) return;
  if (eventSourceListeners.has(eventType)) return;
  const fn = (e: MessageEvent) => {
    let parsed: any = e.data;
    try { parsed = JSON.parse(e.data); } catch { /* leave as raw string */ }
    const set = listeners.get(eventType);
    if (!set) return;
    for (const handler of set) {
      try { handler(parsed); } catch (err) { console.warn('[sseHub]', eventType, err); }
    }
  };
  es.addEventListener(eventType, fn as EventListener);
  eventSourceListeners.set(eventType, fn);
}

function openConnection() {
  if (es) return;
  if (typeof EventSource === 'undefined') {
    setOnline(false);
    return;
  }
  es = new EventSource(STREAM_URL);
  es.onopen = () => setOnline(true);
  es.onerror = () => {
    // EventSource reconnects automatically; just flip the flag so consumers
    // can fall back to polling until onopen fires again.
    setOnline(false);
  };
  // Re-attach any listeners registered before the connection existed.
  for (const eventType of listeners.keys()) ensureNativeListener(eventType);
}

function closeConnection() {
  if (!es) return;
  for (const [eventType, fn] of eventSourceListeners) {
    try { es.removeEventListener(eventType, fn as EventListener); } catch { /* noop */ }
  }
  eventSourceListeners.clear();
  try { es.close(); } catch { /* noop */ }
  es = null;
  setOnline(false);
}

/**
 * Subscribe to a server-sent event by name. Returns an unsubscribe function.
 * Connection is opened on first subscribe and closed (after a grace period)
 * when the last subscriber leaves.
 */
export function subscribe<T = any>(eventType: string, handler: SSEHandler<T>): () => void {
  let set = listeners.get(eventType);
  if (!set) {
    set = new Set();
    listeners.set(eventType, set);
  }
  set.add(handler as Listener);

  if (closeTimer != null) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  if (!es) openConnection();
  else ensureNativeListener(eventType);

  return () => {
    const s = listeners.get(eventType);
    if (!s) return;
    s.delete(handler as Listener);
    if (s.size === 0) listeners.delete(eventType);

    if (totalSubscriberCount() === 0 && es) {
      if (closeTimer != null) clearTimeout(closeTimer);
      closeTimer = (typeof window !== 'undefined' ? window.setTimeout : setTimeout)(
        () => {
          closeTimer = null;
          if (totalSubscriberCount() === 0) closeConnection();
        },
        CLOSE_GRACE_MS,
      ) as unknown as number;
    }
  };
}

/**
 * Subscribe to connection-status changes. Handler is invoked with the current
 * value immediately on subscribe. Returns an unsubscribe function.
 */
export function subscribeStatus(handler: StatusHandler): () => void {
  statusListeners.add(handler);
  try { handler(online); } catch { /* noop */ }
  return () => { statusListeners.delete(handler); };
}

/** Read the current online flag synchronously. */
export function isOnline(): boolean {
  return online;
}

export const sseHub = { subscribe, subscribeStatus, isOnline };
export default sseHub;
